// Timeline grafiğini ekran-dışı, B&W temalı bir container'a çizip
// html-to-image (foreignObject → tarayıcının kendisi çizer) ile PNG'ye yakalar.
// foreignObject yaklaşımı writing-mode/rotate, ::after üçgenleri,
// repeating-linear-gradient ve var() renklerini birebir korur.

import { toPng, toJpeg } from 'html-to-image';
import { buildModel, buildTimelineHTML } from '../ZamanAsimiTimeline';
import './raporTheme.css';

// Çok geniş grafiklerde tarayıcı canvas boyut limitine takılmamak için
// çıktı piksel genişliği bu değerin altında tutulur.
const MAX_CANVAS_PX = 14000;

/** Web fontları yüklenmeden yakalarsak tabular-nums hizası kayar. */
export async function fontlariBekle() {
  if (!document.fonts || !document.fonts.ready) return;
  try {
    await document.fonts.ready;
    await Promise.all([
      document.fonts.load('500 12px "IBM Plex Mono"'),
      document.fonts.load('400 12px "IBM Plex Sans"'),
      document.fonts.load('700 22px "Spectral"'),
      document.fonts.load('400 13px "Hanken Grotesk"'),
    ]);
  } catch (_) {
    /* font yoksa fallback ile devam */
  }
}

/**
 * Verilen DOM düğümünü görüntü data-URL'ine çevirir; gerçek piksel boyutunu döndürür.
 * @param {HTMLElement} node yakalanacak düğüm
 * @param {{format?: 'png'|'jpeg', quality?: number}} [opts]
 *   format: 'png' (varsayılan, kayıpsız) veya 'jpeg' (PDF için küçük dosya).
 */
export async function nodeToImage(node, { format = 'png', quality = 0.92 } = {}) {
  const width = Math.ceil(node.scrollWidth);
  const height = Math.ceil(node.scrollHeight);
  // çıktı genişliği MAX_CANVAS_PX altında kalsın; metin için 3x'e kadar keskinlik
  const pixelRatio = Math.max(0.75, Math.min(3, MAX_CANVAS_PX / Math.max(width, 1)));

  // ÖNEMLİ: yakalama, ekran-dışı (position:fixed; left:-100000px) bir host
  // üzerinde yapıldığında html-to-image bu inline stili klona da kopyalar ve
  // içerik SVG/foreignObject kadrajının dışına düşüp BOŞ (beyaz) raster üretir.
  // Bu yüzden klonda konumlandırmayı nötrleyip sol-üste sabitliyoruz.
  const commonOptions = {
    width,
    height,
    backgroundColor: '#ffffff',
    pixelRatio,
    cacheBust: true,
    style: { margin: '0', transform: 'none', position: 'static', left: '0px', top: '0px' },
  };

  const dataUrl =
    format === 'jpeg' ? await toJpeg(node, { ...commonOptions, quality }) : await toPng(node, commonOptions);

  const dims = await imageBoyutu(dataUrl);
  return { dataUrl, width: dims.width, height: dims.height, aspect: dims.width / dims.height, format };
}

/** Geriye dönük uyum: PNG yakalama. */
export function nodeToPng(node) {
  return nodeToImage(node, { format: 'png' });
}

/**
 * sonuc'tan timeline grafiğini B&W görüntü olarak yakalar.
 * @param {{format?: 'png'|'jpeg', quality?: number}} [opts] Word PNG ister (varsayılan);
 *   PDF, dosya boyutunu küçültmek için JPEG isteyebilir.
 */
export async function captureTimeline(timelineModel, sonuc, opts) {
  const c = buildModel(timelineModel, sonuc);
  const { nleHTML } = buildTimelineHTML(c);

  const host = document.createElement('div');
  host.className = 'zsh zsh--report';
  host.setAttribute('style', 'position:fixed;left:-100000px;top:0;background:#ffffff;margin:0;padding:0;z-index:-1;');
  host.innerHTML = `<div class="nle"><div class="nle-inner">${nleHTML}</div></div>`;
  document.body.appendChild(host);

  try {
    await fontlariBekle();
    const node = host.querySelector('.nle-inner');
    return await nodeToImage(node, opts);
  } finally {
    host.remove();
  }
}

function imageBoyutu(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = dataUrl;
  });
}
